/* ============================================================
   Страница проекта: обложка, статус, описание и текст.
   Открывается с карточки на /projects и по ссылке #project/<id>.
   ============================================================ */
import { useEffect } from 'react';
import { useFedStore, useL } from '../store';
import { useFedContent, uploadUrl } from '../content';
import { cover } from '../data';
import { Ic } from '../icons';
import { Rv } from '../ui';

export default function FedProject() {
  const { go, lang, projectId } = useFedStore();
  const L = useL();
  const projects = useFedContent((s) => s.projects);

  const p = projects.find((x) => x.id === projectId) || projects[0];

  useEffect(() => { window.scrollTo(0, 0); }, [projectId]);

  if (!p) {
    return (
      <div className="fed-pagewrap max-w-[960px] mx-auto" style={{ padding: '52px 24px 72px' }}>
        <h1 style={{ fontSize: 'clamp(28px,4.6vw,44px)' }}>{L.proj_h1}</h1>
        <p className="text-fg-muted" style={{ marginTop: 14, fontSize: 15 }}>Проект не найден.</p>
        <button onClick={() => go('projects')} className="fed-btn-ghost h-11 px-5 rounded-[11px]" style={{ fontSize: 14, marginTop: 20 }}>
          ← {L.proj_h1}
        </button>
      </div>
    );
  }

  const active = p.status === 'active';
  const body = p['body_' + lang] || p.body_ru || [];
  const others = projects.filter((x) => x.id !== p.id).slice(0, 3);

  return (
    <div className="fed-pagewrap max-w-[960px] mx-auto" style={{ padding: '28px 24px 72px' }}>
      <button
        onClick={() => go('projects')}
        className="bg-transparent border-none cursor-pointer text-fg-muted font-display font-semibold uppercase hover:text-fg"
        style={{ fontSize: 14, letterSpacing: '.04em', padding: '6px 0', marginBottom: 16 }}
      >
        ← {L.proj_h1}
      </button>

      <Rv className="flex items-center gap-2.5 flex-wrap" style={{ marginBottom: 14 }}>
        <span
          className="inline-flex items-center gap-1.5 rounded-full font-mono uppercase border border-line"
          style={{
            padding: '5px 11px', fontSize: 10.5, letterSpacing: '.1em',
            background: active ? 'rgba(67,217,106,.12)' : 'rgba(230,183,60,.12)',
            color: active ? 'var(--fed-ok)' : 'var(--fed-gold)',
          }}
        >
          {active ? L.proj_active : L.proj_soon}
        </span>
        {p['meta_' + lang] ? (
          <span className="font-mono uppercase text-gold" style={{ fontSize: 11, letterSpacing: '.12em' }}>{p['meta_' + lang]}</span>
        ) : null}
      </Rv>

      <Rv as="h1" style={{ fontSize: 'clamp(30px,5vw,52px)' }}>{p['title_' + lang]}</Rv>

      {/* обложка: картинка из админки, иначе плашка с иконкой проекта */}
      <Rv
        className="relative overflow-hidden rounded-[18px] shadow-card flex items-center justify-center"
        style={{ height: 'clamp(180px,38vw,320px)', margin: '24px 0', background: cover(p.hue ?? 8, 24, 10) }}
      >
        {p.cover
          ? <img src={uploadUrl(p.cover)} alt="" className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover' }} />
          : (
            <span className="flex items-center justify-center rounded-[22px] text-accent-bright" style={{ width: 96, height: 96, background: 'rgba(225,18,23,.12)', border: '1px solid var(--fed-line-accent)' }}>
              <Ic name={p.ic || 'layers'} size={44} />
            </span>
          )}
      </Rv>

      {p['d_' + lang] ? (
        <Rv as="p" className="text-fg-soft font-semibold" style={{ fontSize: 18, lineHeight: 1.6, marginBottom: 22 }}>
          {p['d_' + lang]}
        </Rv>
      ) : null}

      {body.map((para, i) => (
        <Rv as="p" key={i} className="text-fg-soft" style={{ fontSize: 16, lineHeight: 1.78, marginBottom: 18 }}>
          {para}
        </Rv>
      ))}

      {p.link ? (
        <Rv style={{ marginTop: 10 }}>
          <a
            href={p.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-[11px] no-underline font-display font-bold uppercase"
            // цвет инлайном: .fed-root a красит все ссылки в акцент и перебивает text-white
            style={{ fontSize: 13, letterSpacing: '.03em', color: '#fff', background: 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))', boxShadow: '0 10px 24px -10px var(--fed-accent-glow)' }}
          >
            <Ic name="external" size={16} /> Подробнее
          </a>
        </Rv>
      ) : null}

      {others.length > 0 && (
        <>
          <Rv style={{ marginTop: 48, marginBottom: 16 }}>
            <h2 style={{ fontSize: 'clamp(22px,3vw,30px)' }}>{L.proj_h1}</h2>
          </Rv>
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))' }}>
            {others.map((o, i) => (
              <Rv
                key={o.id}
                delay={i * 0.07}
                onClick={() => go('project', { projectId: o.id })}
                className="fed-hover-lift cursor-pointer flex flex-col gap-2.5 rounded-[16px] border border-line"
                style={{ padding: 20, background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}
              >
                <span className="flex items-center justify-center rounded-[12px] text-accent-bright" style={{ width: 42, height: 42, background: 'rgba(225,18,23,.09)', border: '1px solid var(--fed-line-accent)' }}>
                  <Ic name={o.ic || 'layers'} size={20} />
                </span>
                <div className="font-display font-semibold text-fg" style={{ fontSize: 17 }}>{o['title_' + lang]}</div>
                <div className="text-fg-muted" style={{ fontSize: 13, lineHeight: 1.55 }}>{o['d_' + lang]}</div>
              </Rv>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
